import { useEffect } from 'react';
import { styled } from 'styled-components';
import { EventEcgCardioExaminationDuration } from '@common/enums/eventEcgCardioExaminationDuration';
import { getSelectedLanguage } from '@frontend/translate';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { ICustomControlData } from './customControlData';
import { HolterCardioExaminationDuration } from '../../../../../../common/enums/holterCardioExaminationDuration';
import { TcohProcedureType } from '../../../../../../common/enums/tcohProcedureType';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { EnumDropdownInput } from '../../../../fields/EnumDropdown';

type OpnameduurAddEditProps = {
    customControlData: ICustomControlData;
    errors: ValidationErrors;
    onChange: (
        key: keyof ICustomControlData,
        value: HolterCardioExaminationDuration | EventEcgCardioExaminationDuration | null
    ) => void;
    onDoValidate: (key: keyof ICustomControlData) => void;
};

export function OpnameduurAddEdit({
    errors,
    customControlData,
    onChange,
    onDoValidate,
}: Readonly<OpnameduurAddEditProps>) {
    const { eventEcgCardioExaminationDurationEnum: translateHolterDuration, holter: translateHolter } =
        getSelectedLanguage();
    const { procedureType } = customControlData;
    const hasErrors =
        errors.has('customControlData.holterCardioExaminationDuration') ||
        errors.has('customControlData.eventEcgCardioExaminationDuration');

    useEffect(() => {
        if (procedureType === TcohProcedureType.eventEcg) {
            onChange('eventEcgCardioExaminationDuration', EventEcgCardioExaminationDuration.sevenDays);
            onDoValidate('eventEcgCardioExaminationDuration');
        }
    }, [procedureType]);

    return (
        <HorizontalFormGroup hasErrors={hasErrors}>
            <FormLabel isRequired={procedureType !== TcohProcedureType.eventEcg}>Opnameduur</FormLabel>
            <StyledInputContainer>
                {(!procedureType || procedureType === TcohProcedureType.holter) && (
                    <EnumDropdownInput
                        disabled={!procedureType}
                        enumName="holterCardioExaminationDuration"
                        error={errors.get('customControlData.holterCardioExaminationDuration')}
                        fieldKey="holterCardioExaminationDuration"
                        jsEnum={HolterCardioExaminationDuration}
                        onChange={(value) => onChange('holterCardioExaminationDuration', value)}
                        onDoValidate={() => onDoValidate('holterCardioExaminationDuration')}
                        placeholder={translateHolter.chooseDuration}
                    />
                )}

                {procedureType &&
                    procedureType === TcohProcedureType.eventEcg &&
                    customControlData.eventEcgCardioExaminationDuration && (
                        <>{translateHolterDuration[customControlData.eventEcgCardioExaminationDuration]}</>
                    )}
            </StyledInputContainer>
        </HorizontalFormGroup>
    );
}

const StyledInputContainer = styled(InputContainer)`
    display: flex;
    align-items: center;
`;
